if (typeof ProductGallery !== 'function') {
	class ProductGallery extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				wrapper: '.product-gallery__list',
				images: '.product-gallery__list-item',
				variantImages: '.product-gallery__list-item--variant',
				visibleImages: '.product-gallery__list-item--is-visible',
				pagination: '.product-gallery__pagination',
				paginationItems: '.product-gallery__pagination-item',
				headerGroup: '.shopify-section-group-header-group',
				inputs: '.product-form__input',
				variantMediaJS: '.js-data-variant-media',
				deferredMedia: 'deferred-media',
				lightbox: 'product-gallery-lightbox',
			};

			this.classes = {
				selectedImage: 'product-gallery__list-item--is-selected',
				visibleImage: 'product-gallery__list-item--is-visible',
				listItemTopBorder: 'product-gallery__list-item--top-border',
				listItemBottomBorder: 'product-gallery__list-item--bottom-border',
				singleImage: 'product-gallery__list-item--single',
				paginationLoaded: 'product-gallery__pagination--loaded',
				paginationItem: 'product-gallery__pagination-item',
				selectedPaginationItem: 'product-gallery__pagination-item--is-selected',
				passed: 'product-gallery--passed',
				formDropdown: 'product-form__input--dropdown',
			};

			// Elements
			this.parent = this.closest('.product-main') || this.closest('.product-featured');
			this.images = this.querySelectorAll(this.selectors.images);
			this.wrapper = this.querySelector(this.selectors.wrapper);
			this.pagination = this.querySelector(this.selectors.pagination);
			this.headerGroup = Array.from(document.querySelectorAll(this.selectors.headerGroup));
			this.breadcrumbs = document.querySelector('.breadcrumbs');
			this.variantMediaJS = this.querySelector(this.selectors.variantMediaJS);
			this.deferredMedia = this.querySelectorAll(this.selectors.deferredMedia);
			this.lightbox = this.querySelector(this.selectors.lightbox);

			// Variables
			this.headerGroupHeight = this.headerGroup.reduce((total, element) => total + element.clientHeight, 0);
			this.breadcrumbsHeight = this.breadcrumbs ? this.breadcrumbs.offsetHeight : 0;

			// Flags
			this.mediaGroupingEnabled = this.dataset.mediaGroup ? JSON.parse(this.dataset.mediaGroup) : null;
			this.showGeneralMedia = this.dataset.generalMedia ? JSON.parse(this.dataset.generalMedia) : null;
			this.generalMediaOrder = this.dataset.generalMediaOrder ? this.dataset.generalMediaOrder : null;
			this.isHorizontal = this.dataset.isHorizontal ? JSON.parse(this.dataset.isHorizontal) : null;
			this.variantSelected = this.dataset.variantSelected;
			this.onlyFeaturedMedia = this.dataset.onlyFeaturedMedia ? JSON.parse(this.dataset.onlyFeaturedMedia) : null;
			this.galleryHeight = 0;
			this.handleHeight = this.isHorizontal ? 20 : 30;
			this.imagesArray = this.mediaGroupingEnabled && this.hasVisibleImages() ? this.visibleImages : this.images;

			// Methods
			this.init();
		}

		init() {
			this.addEventListeners();
			this.gallerySetup();
		}

		addEventListeners() {
			const { wrapper } = this;

			wrapper.addEventListener('scroll', () => {
				wrapper.dispatchEvent(new CustomEvent('gallery:updated'));
			});

			this.debouncedOnUpdateGallery = debounce((event) => {
				this.updateGallery();
			}, 10);

			wrapper.addEventListener('gallery:updated', this.debouncedOnUpdateGallery.bind(this));

			this.debouncedOnScroll = debounce((event) => {
				this.galleryPassed();
			}, ON_CHANGE_DEBOUNCE_TIMER);

			window.addEventListener('scroll', this.debouncedOnScroll.bind(this));

			this.debouncedOnResize = debounce((event) => {
				this.adjustHeight();
			}, ON_CHANGE_DEBOUNCE_TIMER);

			window.addEventListener('resize', this.debouncedOnResize.bind(this));

			document.addEventListener(THEME_EVENTS.variantChange, (event) => {
				this.onVariantChange(event);
			});
		}

		gallerySetup() {
			const { mediaGroupingEnabled, onlyFeaturedMedia } = this;

			this.adjustHeight();
			this.groupOptionIndex = this.getMediaGroupData().groupOptionIndex;

			if (onlyFeaturedMedia && !mediaGroupingEnabled) {
				this.featuredMediaGallery();
			} else {
				if (mediaGroupingEnabled) {
					this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
				} else {
					this.setVisibleItems();
				}

				this.resetGallery();
			}

			this.lightBoxSetup();
		}

		lightBoxSetup() {
			const { lightbox } = this;

			if (lightbox) lightbox.init();
		}

		paginationSetup() {
			const { imagesArray, pagination, classes, currentImageIndex, galleryHeight } = this;

			pagination.innerHTML = '';

			const imagesLength = imagesArray.length;

			if (imagesLength > 1) {
				const paginationItemSize = 100 / imagesLength;

				pagination.style.setProperty('--pagination-item-size', `${paginationItemSize}%`);

				for (let i = 0; i < imagesLength; i++) {
					const paginationItem = document.createElement('li');

					paginationItem.classList.add(classes.paginationItem);

					if (i == currentImageIndex) paginationItem.classList.add(classes.selectedPaginationItem);

					paginationItem.setAttribute('data-index', i);
					pagination.appendChild(paginationItem);
				}

				pagination.classList.add(classes.paginationLoaded);
			}
		}

		hasVisibleImages() {
			return this.visibleImages && this.visibleImages.length > 0;
		}

		updateGallery() {
			const { wrapper, isHorizontal } = this;

			if (isHorizontal) {
				const scroll = wrapper.scrollLeft;

				this.currentImageIndex = Math.round(scroll / this.imagesArray[0].offsetWidth);
			} else {
				const scroll = wrapper.scrollTop;

				this.currentImageIndex = Math.round(scroll / this.imagesArray[0].offsetHeight);
			}

			if (this.imagesArray[this.currentImageIndex] != this.selectedImage) {
				this.selectedImage = this.imagesArray[this.currentImageIndex];
				this.setActiveImage();
				this.setActivePagination();
			}
		}

		resetGallery() {
			const { images, mediaGroupingEnabled, variantSelected } = this;

			this.imagesArray = mediaGroupingEnabled && this.hasVisibleImages() ? this.visibleImages : images;

			if (!mediaGroupingEnabled && variantSelected && this.dataset.variantCount != '1') {
				const variantMedia = this.getVariantSelectedMedia();

				if (variantMedia) {
					const variantMediaIndex = parseInt(variantMedia.dataset.index);

					this.currentImageIndex = variantMediaIndex;
					this.selectedImage = this.imagesArray[variantMediaIndex];
					this.scrollToImage(this.selectedImage);
				} else {
					this.currentImageIndex = 0;
					this.selectedImage = this.imagesArray[0];
					this.setGalleryScrollPos();
				}
			} else {
				this.currentImageIndex = 0;
				this.selectedImage = this.imagesArray[0];
				this.setGalleryScrollPos();
			}

			this.setActiveImage();
			this.paginationSetup();
			this.setActivePagination();
			this.setGalleryScrollPos();
		}

		featuredMediaGallery() {
			const { images, classes } = this;
			const allImages = [...images];
			const variantImages = [...this.querySelectorAll(this.selectors.variantImages)];
			const firstVariantIndex = allImages.findIndex((img) => variantImages.includes(img));
			const defaultImages = firstVariantIndex >= 0 ? allImages.slice(0, firstVariantIndex) : [...allImages];

			this.imagesArray = [...new Set([...defaultImages, ...variantImages])];

			this.images.forEach((el) => {
				el.setAttribute('data-index', -1);
				el.classList.remove(classes.visibleImage, classes.singleImage);
			});

			this.imagesArray.forEach((el, index) => {
				el.classList.add(classes.visibleImage);
				el.setAttribute('data-index', index);
			});

			this.currentImageIndex = 0;
			this.selectedImage = this.imagesArray[0] || null;
			this.setActiveImage();
			this.paginationSetup();
			this.setActivePagination();
			this.setGalleryScrollPos();
		}

		getVariantSelectedMedia() {
			const { wrapper, variantSelected } = this;
			const variantId = parseInt(variantSelected);
			const variantMedia = this.getMediaGroupData().variantMedia;

			let matchingVariant = null;
			let mediaFound = null;

			if (variantMedia) {
				for (const media of variantMedia) {
					if (media.variantId === variantId) {
						matchingVariant = media.mediaId;

						break;
					}
				}
			}

			if (matchingVariant) {
				mediaFound = wrapper.querySelector(`[data-media-id="${matchingVariant}"]`);
			}

			return mediaFound;
		}

		setActiveImage() {
			const { imagesArray, classes } = this;

			if (imagesArray.length > 0) {
				imagesArray.forEach((image) => image.classList.remove(classes.selectedImage));

				if (!this.selectedImage) {
					imagesArray[0].classList.add(classes.selectedImage);
				} else {
					this.selectedImage.classList.add(classes.selectedImage);
				}
			}
		}

		setActivePagination() {
			const { pagination, classes, selectors, currentImageIndex } = this;
			const paginationItems = pagination.querySelectorAll(selectors.paginationItems);

			if (paginationItems.length > 0) {
				paginationItems.forEach((paginationItem) =>
					paginationItem.classList.remove(classes.selectedPaginationItem)
				);
				pagination
					.querySelector(`[data-index="${currentImageIndex}"]`)
					.classList.add(classes.selectedPaginationItem);
			}
		}

		setGalleryScrollPos() {
			const { wrapper, isHorizontal } = this;

			if (this.isMobileScreen()) {
				if (!isHorizontal) {
					wrapper.scrollTo({ top: 0, behavior: 'instant' });
				} else {
					wrapper.scrollTo({ left: 0, behavior: 'instant' });
				}
			}
		}

		onVariantChange(event) {
			const { mediaGroupingEnabled, groupOptionIndex } = this;

			if (event.detail.sectionId && event.detail.sectionId.includes('quickshop')) return;

			const inputGroupIndex = event.detail.inputGroupIndex;

			if (inputGroupIndex === groupOptionIndex) {
				this.scrollToGallery();
			}

			if (mediaGroupingEnabled) {
				this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());

				if (event.detail.variant && event.detail.variant.featured_media) {
					const variantMedia = this.querySelector(
						`[data-media-id="${event.detail.variant.featured_media.id}"]`
					);

					this.customSetActiveMedia(variantMedia, false);
				}

				if (inputGroupIndex === groupOptionIndex) {
					this.resetGallery();
					this.resetAllMedia();
				}
			} else {
				if (inputGroupIndex === groupOptionIndex) {
					const variantMediaID = event.detail.variant.featured_media.id;
					const imageSource = this.querySelector(`[data-media-id="${variantMediaID}"]`);

					if (imageSource) {
						this.scrollToImage(imageSource);
					}
				}
			}
		}

		scrollToGallery() {
			const topPosition = this.getBoundingClientRect().top + window.scrollY - 20;

			window.scrollTo({ top: topPosition, behavior: 'smooth' });
		}

		scrollToImage(image) {
			const { wrapper, isHorizontal } = this;
			const targetRect = image.getBoundingClientRect();
			const wrapperRect = wrapper.getBoundingClientRect();

			if (this.isMobileScreen()) {
				if (!isHorizontal) {
					const scrollPosition = targetRect.top + wrapper.scrollTop - wrapperRect.top;

					wrapper.scrollTo({ top: scrollPosition, behavior: 'instant' });
				} else {
					const scrollPosition = targetRect.left + wrapper.scrollLeft - wrapperRect.left;

					wrapper.scrollTo({ left: scrollPosition, behavior: 'instant' });
				}
			} else {
				const scrollPosition = targetRect.top + window.scrollY;

				window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
			}
		}

		adjustHeight() {
			const { headerGroupHeight, breadcrumbsHeight, handleHeight } = this;

			this.windowHeight = document.documentElement.clientHeight;
			this.galleryHeight = this.windowHeight - headerGroupHeight - breadcrumbsHeight - handleHeight;

			if (this.isMobileScreen()) {
				// this.style.setProperty('--gallery-height', this.galleryHeight + 'px');
			} else {
				this.style.removeProperty('--gallery-height');
			}
		}

		resetAllMedia() {
			const { deferredMedia } = this;

			deferredMedia.forEach((media) => {
				if (media.getAttribute('loaded')) {
					media.removeAttribute('loaded');
					media.querySelector('video').remove();
				}
			});
		}

		galleryPassed() {
			const { wrapper, classes } = this;
			const galleryRect = wrapper.getBoundingClientRect();
			const galleryTop = galleryRect.top + window.scrollY;
			const galleryBottom = galleryTop + this.galleryHeight;
			const maxScrollDistance = this.galleryHeight;
			const scrollY = window.scrollY;
			const viewportHeight = window.innerHeight;

			if (this.isMobileScreen()) {
				if (scrollY + viewportHeight - maxScrollDistance >= galleryBottom) {
					this.classList.add(classes.passed);
				} else {
					this.classList.remove(classes.passed);
				}
			} else {
				this.classList.remove(classes.passed);
			}
		}

		isMobileScreen() {
			return window.matchMedia('(max-width: 40em)').matches;
		}

		setGroupOptionName() {
			const { variantMediaJS } = this;

			if (typeof this.groupOptionName === 'undefined') {
				const dataEl = variantMediaJS;

				try {
					const jsonData = dataEl ? JSON.parse(dataEl.textContent) : null;

					if (jsonData !== null) {
						this.groupOptionName = jsonData.groupOptionName;
					} else {
						this.groupOptionName = false;
					}
				} catch (error) {
					this.groupOptionName = false;
				}
			}
		}

		getMediaGroupData() {
			const { variantMediaJS } = this;

			if (typeof this.variantMediaData === 'undefined') {
				const dataEl = variantMediaJS;

				try {
					const jsonData = dataEl ? JSON.parse(dataEl.textContent) : null;

					if (jsonData !== null) {
						this.variantMediaData = jsonData;
					} else {
						this.variantMediaData = false;
					}
				} catch (error) {
					this.variantMediaData = false;
				}
			}

			return this.variantMediaData;
		}

		getMediaGroupFromOptionSelectors() {
			const { parent, selectors, classes } = this;
			const optionSelectors = parent.querySelectorAll(selectors.inputs);

			if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
				const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];

				if (selector.classList.contains(classes.formDropdown)) {
					return selector.querySelector('select').value;
				} else {
					return selector.querySelector('input:checked').value;
				}
			}
			return null;
		}

		getMediaGroupMap() {
			if (!this.mediaGroupMap) {
				this.mediaGroupMap = {
					groups: {},
				};

				const variantMediaData = this.getMediaGroupData();
				let currentMediaOptionName = false;

				this.viewerItems = this.images;
				this.viewerItems.forEach((item) => {
					if (!variantMediaData.variantMedia) return;

					for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
						if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
							if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
								currentMediaOptionName = variantMediaData.variantMedia[i].option;
							}
						}
					}
					if (currentMediaOptionName) {
						if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
							this.mediaGroupMap.groups[currentMediaOptionName] = {
								name: currentMediaOptionName,
								items: [],
							};
						}
						const groupItem = { main: item };

						this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
					}
				});

				this.mediaGroupMap.groupFromItem = (item) => {
					const groups = Object.keys(this.mediaGroupMap.groups);

					for (let i = 0; i < groups; i += 1) {
						const group = groups[i];

						for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
							if (this.mediaGroupMap.groups[group].items[j] === item) {
								return this.mediaGroupMap.groups[group];
							}
						}
					}

					return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
				};
			}

			return this.mediaGroupMap;
		}

		setActiveMediaGroup(groupName) {
			const { wrapper, images, showGeneralMedia, generalMediaOrder, classes } = this;
			const mediaGroupMap = this.getMediaGroupMap();
			const selectedGroup = groupName
				? mediaGroupMap.groups[groupName]
				: mediaGroupMap.groupFromItem(this.currentItem);

			if ((this.currentItem, selectedGroup)) {
				if (this.currentGroup !== selectedGroup) {
					this.currentGroup = selectedGroup;
					this.viewerItems.forEach((item) => {
						item.setAttribute('data-index', -1);
						item.classList.remove(classes.visibleImage, classes.singleImage);
					});

					let currentItemIsVisible = false;

					if (showGeneralMedia) {
						let generalImagesGroup = [];
						const firstGroupImage =
							mediaGroupMap.groups?.[Object.keys(mediaGroupMap.groups)[0]]?.items?.[0]?.main ?? null;

						if (firstGroupImage) {
							for (let i = 0; i < images.length; i++) {
								const generalImage = this.images[i];

								if (generalImage === firstGroupImage) break;
								if (generalImagesGroup.some((item) => item.main === generalImage)) continue;
								if (generalMediaOrder && generalMediaOrder == 'after') {
									wrapper.insertBefore(generalImage, null);
								}

								const generalMainImage = { main: generalImage };

								generalImagesGroup.push(generalMainImage);
							}

							const uniqueGeneralImagesGroup = generalImagesGroup.filter((generalImage) => {
								return !selectedGroup.items.some((item) => item.main === generalImage.main);
							});

							if (generalMediaOrder && generalMediaOrder == 'after') {
								selectedGroup.items = [...selectedGroup.items, ...uniqueGeneralImagesGroup];
							} else {
								selectedGroup.items = [...uniqueGeneralImagesGroup, ...selectedGroup.items];
							}
						}
					}

					selectedGroup.items.forEach((item, index) => {
						item.main.classList.add(classes.visibleImage);
						item.main.setAttribute('data-index', index);

						if (item.main === this.currentItem) {
							currentItemIsVisible = true;
						}
					});

					this.setVisibleItems();

					if (!currentItemIsVisible) {
						this.customSetActiveMedia(selectedGroup.items[0].main, true);
					}

					if (selectedGroup.items.length === 1) {
						selectedGroup.items[0].main.classList.add(classes.singleImage);
					}
				}
			} else {
				this.setVisibleItems();
			}
		}

		setVisibleItems() {
			const { images, classes, mediaGroupingEnabled } = this;

			this.viewerItems = images;
			this.visibleImages = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);

			if (mediaGroupingEnabled) return;

			this.viewerItems.forEach((el) => {
				el.classList.remove(classes.visibleImage);
			});

			this.visibleImages.forEach((el) => {
				el.classList.add(classes.visibleImage);
			});
		}

		customSetActiveMedia(mediaItem) {
			if (mediaItem === this.currentItem) return;

			this.currentItem = mediaItem;
			this.currentIndex = this.visibleImages.indexOf(this.currentItem);
		}
	}

	class ProductGalleryLightbox extends HTMLElement {
		constructor() {
			super();

			this.selectors = {
				gallery: '.product-gallery__list',
				galleryList: '.product-gallery__list',
				visibleImages: '.product-gallery__list-item--is-visible',
				trigger: '.product-gallery__lightbox-trigger',
				template: '.product-gallery__lightbox-template',
				close: '.product-gallery__lightbox-close',
				overlay: '.product-gallery__lightbox-overlay',
				media: '.product-gallery__lightbox-media',
				prev: '.product-gallery__lightbox-prev',
				next: '.product-gallery__lightbox-next',
				counter: '.product-gallery__lightbox-counter',
			};

			this.classes = {
				lightbox: 'product-gallery__lightbox',
				lightBoxIsOpen: 'product-gallery__lightbox--is-open',
				lightBoxFadeOut: 'product-gallery__lightbox--fade-out',
				blockedScroll: 'blocked-scroll',
				mediaVisible: 'product-gallery__lightbox-media--visible',
				hide: 'hide',
			};

			// Elements
			this.body = document.querySelector('body');
			this.gallery = this.closest('product-gallery').querySelector(this.selectors.galleryList);
			this.triggers = this.closest('product-gallery').querySelectorAll(this.selectors.trigger);
			this.close = this.querySelector(this.selectors.close);
			this.overlay = this.querySelector(this.selectors.overlay);
			this.template = this.querySelector(this.selectors.template);
			this.media = this.querySelector(this.selectors.media);
			this.prev = this.querySelector(this.selectors.prev);
			this.next = this.querySelector(this.selectors.next);
			this.counter = this.querySelector(this.selectors.counter);

			// Variables
			this.lastMediaId = null;
			this.lastMediaIndex = null;
			this.imagesLength = 0;

			// Flags
			this.isFloating = this.dataset.type == 'floating' ? true : false;
			this.hasBackground = this.dataset.hasBg ? JSON.parse(this.dataset.hasBg) : null;
			this.mediaSource = null;
		}

		init() {
			const { gallery, isFloating } = this;

			this.images = gallery.querySelectorAll(this.selectors.visibleImages);
			this.imagesLength = this.images.length;

			this.addEventListeners();

			if (isFloating) {
				this.handleIntersection();
			}
		}

		addEventListeners() {
			const { triggers, isFloating, close, overlay, prev, next } = this;

			if (isFloating) {
				this.debouncedOnScroll = debounce((event) => {
					this.handleIntersection();
				}, ON_CHANGE_DEBOUNCE_TIMER);

				window.addEventListener('scroll', this.debouncedOnScroll.bind(this));
			}

			triggers.forEach((trigger) => {
				trigger.addEventListener('click', (event) => this.openLightBox(event));
			});

			close.addEventListener('click', this.closeLightBox.bind(this));
			overlay.addEventListener('click', this.closeLightBox.bind(this));

			document.addEventListener('keydown', (event) => {
				if (event.key === 'Escape') {
					this.closeLightBox();
				}
			});

			prev.addEventListener('click', this.setPreviousMedia.bind(this));
			next.addEventListener('click', this.setNextMedia.bind(this));

			document.addEventListener(THEME_EVENTS.variantChange, () => {
				this.onVariantChange();
			});
		}

		onVariantChange() {
			const { gallery, isFloating } = this;

			this.images = gallery.querySelectorAll(this.selectors.visibleImages);
			this.imagesLength = this.images.length;

			if (isFloating) {
				this.handleIntersection();
			}
		}

		handleIntersection() {
			const { triggers, lastMediaId } = this;

			if (this.isMobileScreen()) return;

			const imagesArray = Array.from(this.images);
			const lightBoxRect = triggers[0].getBoundingClientRect();

			for (let i = 0; i <= imagesArray.length; i++) {
				const image = imagesArray[i];

				if (!image) continue;

				const imageRect = image.getBoundingClientRect();

				if (imageRect.bottom > lightBoxRect.top && imageRect.top < lightBoxRect.bottom) {
					if (lastMediaId && lastMediaId == image.dataset.mediaId) continue;

					this.mediaType = image.dataset.mediaType;
					this.mediaUrl = image.dataset.mediaUrl;

					if (image.dataset.mediaSource) this.mediaSource = image.dataset.mediaSource;

					this.lastMediaId = image.dataset.mediaId;
					this.lastMediaIndex = parseInt(image.dataset.index);

					this.updateMediaUrl();
					this.updateCounter();
				}
			}
		}

		updateMediaUrl() {
			const { hasBackground, mediaUrl } = this;

			if (hasBackground) this.style.setProperty('--media-url', `url('${mediaUrl}')`);
		}

		updateCounter() {
			const { counter, lastMediaIndex, imagesLength } = this;

			this.currentCounter = lastMediaIndex + 1;

			const counterTemplate = `${this.currentCounter} / ${imagesLength}`;

			counter.textContent = counterTemplate;

			if (imagesLength === 1) {
				this.hideButtons();
			} else {
				this.showButtons();
			}
		}

		hideButtons() {
			const { prev, next, classes } = this;

			prev.classList.add(classes.hide);
			next.classList.add(classes.hide);
		}

		showButtons() {
			const { prev, next, classes } = this;

			prev.classList.remove(classes.hide);
			next.classList.remove(classes.hide);
		}

		openLightBox(event) {
			const { isFloating, body, classes } = this;

			if (this.isMobileScreen()) return;

			body.classList.add(classes.blockedScroll);
			this.classList.add(classes.lightBoxIsOpen);
			this.ariaHidden = false;
			trapFocus(this);

			if (!isFloating && event.target) {
				const mediaSource = event.target.parentElement;

				if (mediaSource && mediaSource.dataset.mediaUrl) {
					this.mediaType = mediaSource.dataset.mediaType;
					this.mediaUrl = mediaSource.dataset.mediaUrl;

					if (mediaSource.dataset.mediaSource) this.mediaSource = mediaSource.dataset.mediaSource;

					this.lastMediaId = mediaSource.dataset.mediaId;
					this.lastMediaIndex = parseInt(mediaSource.dataset.index);

					this.setActiveMedia(mediaSource);
					this.updateCounter();
				}
			} else {
				this.setActiveMedia();
			}
		}

		closeLightBox() {
			const { body, classes } = this;

			this.clearMedia();

			body.classList.remove(classes.blockedScroll);
			this.classList.remove(classes.lightBoxIsOpen);
			this.ariaHidden = true;
		}

		setActiveMedia() {
			const { media, mediaType, mediaUrl, mediaSource, classes } = this;

			if (!mediaUrl || !mediaType) return;

			if (mediaType === 'image') {
				const mediaElement = document.createElement('img');

				mediaElement.src = mediaUrl;
				media.append(mediaElement);
				mediaElement.onload = () => media.classList.add(classes.mediaVisible);
			} else if (mediaType === 'video') {
				const mediaElement = document.createElement('video');

				media.append(mediaElement);
				mediaElement.src = mediaSource ? mediaSource : mediaUrl;
				mediaElement.muted = true;
				mediaElement.playsInline = true;
				mediaElement.controls = true;

				if (mediaElement.readyState !== 4) {
					mediaElement.load();
				}

				mediaElement.onloadeddata = () => {
					media.classList.add(classes.mediaVisible);
					mediaElement.play();
				};
			}
		}

		setPreviousMedia() {
			const { images, imagesLength, lastMediaIndex } = this;
			const imagesArray = Array.from(images);
			const prevMediaIndex = (lastMediaIndex - 1 + imagesLength) % imagesLength;
			const prevMedia = imagesArray[prevMediaIndex];

			if (!prevMedia) return;

			this.mediaType = prevMedia.dataset.mediaType;
			this.mediaUrl = prevMedia.dataset.mediaUrl;

			if (prevMedia.dataset.mediaSource) this.mediaSource = prevMedia.dataset.mediaSource;

			this.lastMediaId = prevMedia.dataset.mediaId;
			this.lastMediaIndex = prevMediaIndex;

			this.clearMedia();
			this.setActiveMedia(prevMedia);
			this.updateCounter();
		}

		setNextMedia() {
			const { images, imagesLength, lastMediaIndex } = this;
			const imagesArray = Array.from(images);
			const nextMediaIndex = (lastMediaIndex + 1) % imagesLength;
			const nextMedia = imagesArray[nextMediaIndex];

			if (!nextMedia) return;

			this.mediaType = nextMedia.dataset.mediaType;
			this.mediaUrl = nextMedia.dataset.mediaUrl;

			if (nextMedia.dataset.mediaSource) this.mediaSource = nextMedia.dataset.mediaSource;

			this.lastMediaId = nextMedia.dataset.mediaId;
			this.lastMediaIndex = nextMediaIndex;

			this.clearMedia();
			this.setActiveMedia(nextMedia);
			this.updateCounter();
		}

		clearMedia() {
			const { media, classes } = this;

			media.classList.remove(classes.mediaVisible);
			media.innerHTML = '';
		}

		isMobileScreen() {
			return window.matchMedia('(max-width: 40em)').matches;
		}
	}

	if (!customElements.get('product-gallery-lightbox')) {
		customElements.define('product-gallery-lightbox', ProductGalleryLightbox);
	}

	if (!customElements.get('product-gallery')) {
		customElements.define('product-gallery', ProductGallery);
	}
}
